import Big from 'big.js';
import isUUID from 'validator/es/lib/isUUID';
import isEmail from 'validator/lib/isEmail';

import { APIConfig } from 'src/config';
import { convertEnergyPricePartnerToPowertracer } from 'src/util/conversions';
import { isFloatValueAndGTEQZero } from 'src/util/math';
import isDirectionValid from 'src/util/tradeDirection/validation';

import { TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL } from '../constants';

/**
 * Normalises a price value to Powertracer format ($/Wh) as a Big.js instance.
 * @param {import('react-intl').IntlShape} intlCurrency - Currency-aware intl
 *   configuration used for the Partner-to-Powertracer conversion.
 * @param {Big|number|string} price - Price in either Powertracer or Partner format.
 * @returns {Big|null} The price in Powertracer format, or null when conversion fails.
 */
const toPowertracerBig = (intlCurrency, price) => {
  if (price instanceof Big) {
    return price;
  }
  if (typeof price === 'number') {
    return convertEnergyPricePartnerToPowertracer(intlCurrency, price);
  }
  if (typeof price === 'string') {
    return convertEnergyPricePartnerToPowertracer(intlCurrency, parseFloat(price));
  }
  return null;
};

/**
 * Validates the provided proposer trade point ID against the UUID format.
 * @param {string} proposerTradePointId - The proposer trade point ID to validate.
 * @returns {boolean} - True if the provided ID is a valid UUID, false otherwise.
 */
export const isProposerTradePointIdValid = (proposerTradePointId) => (
  proposerTradePointId ? isUUID(proposerTradePointId) : false);

/**
 * Validates a trade rule by checking the following:
 * - The trade direction is valid
 * - The proposer trade point ID is a valid UUID
 * - The recipient user email is valid
 * - All clause prices are valid
 * @param {object} tradeRule - The trade rule to validate.
 * @returns {boolean} - True if the trade rule is valid, false otherwise.
 */
export const isTradeRuleValid = (tradeRule) => {
  if (!tradeRule) {
    return false;
  }
  return isDirectionValid(tradeRule.direction)
  && isProposerTradePointIdValid(tradeRule.proposerTradePointId)
  && isEmail(tradeRule.recipientUserEmail)
  && tradeRule.clauses.every(({ price }) => isFloatValueAndGTEQZero(price));
};

/**
 * Validates a buy price against community trading limits.
 * Accepts either a Big.js value in Powertracer format ($/Wh) or a numeric/string
 * value in the locale-dependent Partner format (e.g. c/kWh in en-AU, yen/kWh in
 * ja-JP). Partner values are converted to Powertracer via
 * convertEnergyPricePartnerToPowertracer before comparison, so the configured
 * COMMUNITY_BUY_PRICE_MIN/MAX bounds from APIConfig() remain in their native
 * Powertracer format.
 * @param {import('react-intl').IntlShape} intlCurrency - Currency-aware intl
 *   configuration used by the Partner-to-Powertracer conversion.
 * @param {Big|number|string} price - Price in Powertracer or Partner format.
 * @returns {boolean} True if price is within the valid buy price range.
 */
export const isBuyPriceValid = (intlCurrency, price) => {
  if (!price) {
    return false;
  }
  const powertracerPrice = toPowertracerBig(intlCurrency, price);
  if (powertracerPrice == null) {
    return false;
  }

  const { COMMUNITY_BUY_PRICE_MAX, COMMUNITY_BUY_PRICE_MIN } = APIConfig();

  if (COMMUNITY_BUY_PRICE_MIN !== undefined && powertracerPrice.lt(COMMUNITY_BUY_PRICE_MIN)) {
    return false;
  }
  if (COMMUNITY_BUY_PRICE_MAX !== undefined && powertracerPrice.gt(COMMUNITY_BUY_PRICE_MAX)) {
    return false;
  }

  return true;
};

/**
 * Validates a sell price against community trading limits.
 * Accepts either a Big.js value in Powertracer format ($/Wh) or a numeric/string
 * value in the locale-dependent Partner format (e.g. c/kWh in en-AU, yen/kWh in
 * ja-JP). Partner values are converted to Powertracer via
 * convertEnergyPricePartnerToPowertracer before comparison, so the configured
 * COMMUNITY_SELL_PRICE_MIN/MAX bounds from APIConfig() remain in their native
 * Powertracer format.
 * @param {import('react-intl').IntlShape} intlCurrency - Currency-aware intl
 *   configuration used by the Partner-to-Powertracer conversion.
 * @param {Big|number|string} price - Price in Powertracer or Partner format.
 * @returns {boolean} True if price is within the valid sell price range.
 */
export const isSellPriceValid = (intlCurrency, price) => {
  if (!price) {
    return false;
  }
  const powertracerPrice = toPowertracerBig(intlCurrency, price);
  if (powertracerPrice == null) {
    return false;
  }

  const { COMMUNITY_SELL_PRICE_MAX, COMMUNITY_SELL_PRICE_MIN } = APIConfig();

  if (COMMUNITY_SELL_PRICE_MIN !== undefined && powertracerPrice.lt(COMMUNITY_SELL_PRICE_MIN)) {
    return false;
  }
  if (COMMUNITY_SELL_PRICE_MAX !== undefined && powertracerPrice.gt(COMMUNITY_SELL_PRICE_MAX)) {
    return false;
  }

  return true;
};

/**
 * Returns a single-argument validator for the given trade direction, bound to
 * the supplied intlCurrency. This shape is preserved so existing call sites
 * (e.g. ClauseEdit's priceValidation prop) continue to receive a
 * `(price) => boolean` function.
 * @param {import('react-intl').IntlShape} intlCurrency - Currency-aware intl
 *   configuration passed through to the underlying validator.
 * @param {TRADE_DIRECTION_BUY | TRADE_DIRECTION_SELL | string} tradeDirection
 * @returns {(price: Big|number|string) => boolean} Validator for the direction.
 */
export const isCommunityPriceValid = (intlCurrency, tradeDirection) => {
  switch (tradeDirection) {
    case TRADE_DIRECTION_BUY:
      return (price) => isBuyPriceValid(intlCurrency, price);
    case TRADE_DIRECTION_SELL:
      return (price) => isSellPriceValid(intlCurrency, price);
    default:
      return () => false;
  }
};
